import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  IconButton,
  InputAdornment,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Divider,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
} from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import TagInputField from "./TagInputField";

const StyledPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(8),
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: "10px",
  boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
}));

const PasswordRequirement = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
  marginTop: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  "&::before": {
    content: '"✓"',
    marginRight: theme.spacing(1),
    color: theme.palette.success.main,
  },
}));

const SocialButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "100%",
  padding: theme.spacing(1.5),
  textTransform: "none",
  fontSize: "1rem",
}));

// const companyTypes = [
//   "startup",
//   "corporate",
//   "scale-up",
//   "family-owned",
//   "mid-size",
//   "other",
// ];

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userType, setUserType] = useState(
    location.search.includes("tab=corporate") ? "corporate" : "candidate",
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    repeatPassword: "",
    // companyValue: "",
    // companyName: "",
    // companyType: "",
    // workEnvironment: [],
    // cultureValue: [],
  });
  const [loading, setLoading] = useState(false);

  const handleUserTypeChange = (event, newValue) => {
    setUserType(newValue);
    setErrors({});
    setServerError("");
    setFormData({
      email: "",
      password: "",
      repeatPassword: "",
      // companyValue: "",
      // companyName: "",
      // companyType: "",
      // workEnvironment: [],
      // cultureValue: [],
    });
    setShowConfirmPassword(false);
    setShowPassword(false);
  };

  const validatePassword = (password, repeatPassword) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character";
    }
    if (repeatPassword && password !== repeatPassword) {
      return "Passwords do not match";
    }
    if (repeatPassword && password === repeatPassword) {
      setErrors((prev) => ({ ...prev, repeatPassword: "" }));
    }
    return "";
  };

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        if (!value) return "Email is required";
        if (!/\S+@\S+\.\S+/.test(value)) return "Invalid email format";
        break;

      case "password":
        if (!value.trim()) return "Password is required";
        return validatePassword(value, formData.repeatPassword);

      case "repeatPassword":
        if (!value.trim()) return "Confirm password is required";
        if (formData.password && value !== formData.password)
          return "Passwords do not match";
        if (value === formData.password) {
          setErrors((prev) => ({ ...prev, password: "", repeatPassword: "" }));
        }
        break;

      // case "companyType":
      //   if (
      //     userType === "corporate" &&
      //     (!value || value === "Select Company Type")
      //   ) {
      //     return "Company type is required";
      //   }
      //   break;

      // case "workEnvironment":
      //   if (userType === "corporate" && !value.length) {
      //     return "At least one work environment is required";
      //   }
      //   break;

      // case "cultureValue":
      //   if (userType === "corporate" && !value.length) {
      //     return "At least one culture value is required";
      //   }
      //   break;

      // case "companyName":
      //   if (userType === "corporate" && !value.trim()) {
      //     return "Company name is required";
      //   }
      //   break;

      // case "companyValue":
      //   if (userType === "corporate" && !value.trim()) {
      //     return "Company value is required";
      //   }
      //   break;

      default:
        break;
    }
    return "";
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Run field validation immediately
      const errorMsg = validateField(name, value);

      setErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    },
    [setFormData, setErrors, validateField],
  );

  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            userType: userType,
            // ...(userType === "corporate" && {
            //   companyName: formData.companyName,
            //   companyType: formData.companyType,
            //   workEnvironment: formData.workEnvironment,
            //   cultureValue: formData.cultureValue,
            //   companyValue: formData.companyValue,
            // }),
          }),
        },
      );

      if (!response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          throw new Error(data.error || "Registration failed");
        } catch {
          throw new Error(`Registration failed: ${text}`);
        }
      }

      setShowSuccess(true);
      setTimeout(() => navigate(`/login?tab=${userType}`), 2000);
    } catch (err) {
      setServerError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google?userType=${userType}`;
  };

  const handleMicrosoftSignUp = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/microsoft?userType=${userType}`;
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{ mb: userType === "corporate" ? 8 : 0 }}
    >
      <StyledPaper>
        <Box
          component="img"
          src="/bb-logo.png"
          alt="Logo"
          sx={{
            height: 70,
            width: 70,
            mb: 2,
            objectFit: "contain",
          }}
        />
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Create an account as
        </Typography>

        {/* Display server error */}
        {serverError && (
          <Alert severity="error" sx={{ width: "90%", mb: 2 }}>
            {serverError}
          </Alert>
        )}

        <Tabs value={userType} onChange={handleUserTypeChange} sx={{ mb: 2 }}>
          <Tab value="candidate" label="Candidate" />
          <Tab value="corporate" label="Corporate" />
        </Tabs>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <TextField
            margin="normal"
            // required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            error={!!errors.email}
            value={formData.email}
            onChange={handleChange}
            helperText={errors.email || ""}
          />

          {/* {userType === "corporate" && (
            <>
              <TextField
                margin="normal"
                // required
                fullWidth
                id="companyName"
                label="Company Name"
                name="companyName"
                autoFocus
                error={!!errors.companyName}
                value={formData.companyName}
                onChange={handleChange}
                helperText={errors.companyName || ""}
              />
              <FormControl
                fullWidth
                sx={{ mt: 2, mb: 2, borderRadius: "4px" }}
                error={!!errors.companyType}
              >
                <InputLabel id="company-value-label">Company Type</InputLabel>
                <Select
                  labelId="company-value-label"
                  id="companyType"
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleChange}
                  displayEmpty
                  label="Company Type"
                >
                  {companyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
                {errors.companyType && (
                  <Typography variant="caption" color="error">
                    {errors.companyType}
                  </Typography>
                )}
              </FormControl>

              <TagInputField
                label="Work Environment"
                name="workEnvironment"
                formData={formData}
                setFormData={setFormData}
                error={errors.workEnvironment}
                setErrors={setErrors}
              />

              <TagInputField
                label="Culture Value"
                name="cultureValue"
                formData={formData}
                setFormData={setFormData}
                error={errors.cultureValue}
                setErrors={setErrors}
              />

              <TextField
                label="Company Value"
                // required
                name="companyValue"
                id="companyValue"
                value={formData.companyValue}
                onChange={handleChange}
                multiline
                fullWidth
                rows={2}
                error={!!errors.companyValue}
                helperText={errors.companyValue || ""}
              />
            </>
          )} */}

          <TextField
            margin="normal"
            // required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password || ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            margin="normal"
            // required
            fullWidth
            name="repeatPassword"
            label="Repeat password"
            type={showConfirmPassword ? "text" : "password"}
            id="repeatPassword"
            value={formData.repeatPassword}
            onChange={handleChange}
            error={!!errors.repeatPassword}
            helperText={errors.repeatPassword || ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 2 }}>
            <PasswordRequirement>
              Password Must be at least 8 characters
            </PasswordRequirement>
            <PasswordRequirement>
              Password Must contain one special character
            </PasswordRequirement>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? "Registering..." : "Get Started"}
          </Button>

          <Divider sx={{ my: 3 }}>OR</Divider>
          <>
            <SocialButton
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignUp}
            >
              Sign up with Google
            </SocialButton>
          </>
          <SocialButton
            variant="outlined"
            startIcon={<MicrosoftIcon />}
            onClick={handleMicrosoftSignUp}
          >
            Sign up with Microsoft
          </SocialButton>

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2">
              Already have an account?{" "}
              <Link
                to={`/login?tab=${userType}`}
                style={{ textDecoration: "none", color: "#1976d2" }}
              >
                Log in
              </Link>
            </Typography>
          </Box>
        </Box>
      </StyledPaper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Registration successful! Redirecting to login page...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Register;
